import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { useTumWebhooks } from '@/hooks/useTumWebhooks';
import { Plus, Trash2, TestTube, CheckCircle2, XCircle, Clock, Settings, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';

export default function TumIntegrations() {
  const { workspaceId } = useParams();
  const { subscriptions, deliveries, isLoading, deleteSubscription, testWebhook, isDeleting, isTesting } = useTumWebhooks();

  const getDeliveryStatus = (delivery: any) => {
    if (delivery.status_code && delivery.status_code >= 200 && delivery.status_code < 300) {
      return { icon: <CheckCircle2 className="h-4 w-4 text-success" />, label: 'Success', color: 'text-success' };
    } else if (delivery.next_retry_at) {
      return { icon: <Clock className="h-4 w-4 text-warning" />, label: 'Retrying', color: 'text-warning' };
    } else {
      return { icon: <XCircle className="h-4 w-4 text-destructive" />, label: 'Failed', color: 'text-destructive' };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Integrations & Webhooks"
          description="Connect TUM with external systems via webhooks and APIs"
        />
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Webhook
        </Button>
      </div>

      {/* BillPortal Parser Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                BillPortal Parser Integration
              </CardTitle>
              <CardDescription>
                Automated bill parsing using BillPortal vendor-specific APIs with OpenAI fallback
              </CardDescription>
            </div>
            <Badge variant="default" className="bg-purple-600">
              Processing Stage
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                <span className="text-sm">Vendor-specific parsing (50+ utilities)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                <span className="text-sm">OpenAI GPT-4 fallback</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                <span className="text-sm">Smart field mapping & confidence scoring</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-success mt-0.5" />
                <span className="text-sm">Real-time statistics & monitoring</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Link to={`/w/${workspaceId}/dashboard/tum/parser-integration`}>
                <Button>
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Parser Integration
                </Button>
              </Link>
              <Link to={`/w/${workspaceId}/dashboard/tum/bill-journey`}>
                <Button variant="outline">
                  View in Bill Journey
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhook Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Webhook Subscriptions</CardTitle>
          <CardDescription>
            Configure webhooks to receive real-time notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading webhooks...</div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="mb-4">No webhooks configured yet</p>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Webhook
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold">{webhook.webhook_name}</h3>
                      <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                        {webhook.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p className="font-mono text-xs">{webhook.endpoint_url}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>Events:</span>
                        {webhook.events.map((event) => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {event}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span>✓ {webhook.success_count} deliveries</span>
                        <span>✗ {webhook.failure_count} failed</span>
                        {webhook.last_triggered_at && (
                          <span>Last: {format(new Date(webhook.last_triggered_at), 'MMM d, HH:mm')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => testWebhook(webhook.id)}
                      disabled={isTesting}
                    >
                      <TestTube className="h-4 w-4 mr-2" />
                      Test
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSubscription(webhook.id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delivery History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Deliveries</CardTitle>
          <CardDescription>Webhook delivery attempts and their results</CardDescription>
        </CardHeader>
        <CardContent>
          {deliveries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No deliveries yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>HTTP Status</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Response</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => {
                  const status = getDeliveryStatus(delivery);
                  return (
                    <TableRow key={delivery.id}>
                      <TableCell>
                        <div className={`flex items-center gap-2 ${status.color}`}>
                          {status.icon}
                          <span>{status.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{delivery.event_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(delivery.created_at), 'MMM d, HH:mm:ss')}
                      </TableCell>
                      <TableCell>
                        {delivery.status_code || '-'}
                      </TableCell>
                      <TableCell>{delivery.retry_count}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {delivery.response_body?.substring(0, 100) || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* API Documentation Card */}
      <Card>
        <CardHeader>
          <CardTitle>API Access</CardTitle>
          <CardDescription>
            RESTful API for programmatic access to TUM data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Base URL</h4>
              <code className="text-sm bg-muted px-2 py-1 rounded">
                https://dyftqspuiyabqzjslwyw.supabase.co/functions/v1/tum-api
              </code>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Authentication</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Include your API key in the request headers:
              </p>
              <code className="text-sm bg-muted px-2 py-1 rounded block">
                Authorization: Bearer YOUR_API_KEY
              </code>
            </div>
            <Button variant="outline">View API Documentation</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
